import { isManualCustomSpell } from '../rules/spellCast.ts';
import { petCheckMode, petCheckBonuses, petEquipmentMaxMana, type PetEquipment } from './petEquipmentService.ts';
import type { ContentRegistry } from '../content/registry.ts';
import type { AttackProfile, Skill } from '../content/types.ts';
import type { PetCompanion } from '../domain/pet.ts';
import { petFloorRank, petMaxHealthSlots } from '../domain/pet.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import type { StatId } from '../domain/stats.ts';
import { STAT_LABELS } from '../domain/stats.ts';
import type { Roller } from '../rules/dice.ts';
import type { RollMode } from '../rules/rollModes.ts';
import { resolveAttackCheck, type AttackCheckOutcome } from '../rules/attackResolution.ts';
import { rollDamage, type DamageRollResult } from '../rules/damageRoll.ts';
import { statModForScore } from '../rules/statModifiers.ts';
import { isOnCooldown, startCooldown } from '../rules/encounter.ts';
import { cooldownScopeFor } from './attackService.ts';
import { healPetHealthSlots } from './petHealthService.ts';
import { spendPetMana } from './petProgressionService.ts';

/**
 * Pet spell execution stays separate from crawler spell execution because pets
 * are not crawlers: their Rank is Floor, they have no Class/Favored surcharge,
 * and they do not earn Skill Advancement marks. Core Rulebook pp. 230-231.
 */
export interface PetSpellAttackPlan {
  equipment?: PetEquipment;
  skill: Skill;
  profile: AttackProfile;
  rank: number;
  toHitStat: StatId;
  toHitMod: number;
  toHitFormula: string;
  damageFormula: string;
  manaCost: number;
  notes: string[];
}

export type PetSupportSpellCast =
  | { ok: true; pet: PetCompanion; manaSpent: number; lines: string[] }
  | { ok: false; reason: string };

function signed(value: number): string {
  return value >= 0 ? `+${value}` : String(value);
}

function assertFloor(floor: number): void {
  if (!Number.isInteger(floor) || floor < 1) throw new Error(`Pet Floor must be at least 1, received ${floor}.`);
}

/** Official default: pet Spell Rank is Floor, while a printed maximum remains a real ceiling. */
export function petSpellRank(skill: Pick<Skill, 'maxRank'>, floor: number): number {
  assertFloor(floor);
  const floorRank = petFloorRank(floor);
  return skill.maxRank === undefined ? floorRank : Math.min(floorRank, skill.maxRank);
}

/**
 * GM-managed exception: an assigned pet Spell may carry an explicit Rank.
 * Missing overrides preserve the official Floor-based default.
 */
export function petAssignedSpellRank(
  pet: Pick<PetCompanion, 'spellRanks'>,
  skill: Pick<Skill, 'id' | 'maxRank'>,
  floor: number,
): number {
  assertFloor(floor);
  const configured = pet.spellRanks[skill.id];
  const base = typeof configured === 'number' && Number.isInteger(configured) && configured >= 1 ? configured : petFloorRank(floor);
  const bounded = Math.min(20, base);
  return skill.maxRank === undefined ? bounded : Math.min(bounded, skill.maxRank);
}

export function petSpellProblem(
  pet: Pick<PetCompanion, 'healthBarSlotsRemaining' | 'attitude' | 'matureBenefit' | 'spellIds' | 'currentMana' | 'unenhancedStats' | 'manaBonus'>,
  skill: Pick<Skill, 'id' | 'name' | 'kind' | 'manaCost'>,
  equipment?: PetEquipment,
): string | null {
  if (pet.healthBarSlotsRemaining <= 0) return 'A dead companion cannot cast Spells.';
  if (pet.attitude !== 'bonded') return 'Only a Bonded pet can cast assigned Spells as a companion.';
  if (pet.matureBenefit?.id !== 'spells') return 'This pet does not have the Level-15 Spells maturity benefit.';
  if (!pet.spellIds.includes(skill.id)) return `${skill.name} is not assigned to this pet.`;
  if (skill.kind !== 'spell' || skill.manaCost === null) return `${skill.name} is not a castable Spell.`;
  const current = Math.min(pet.currentMana, petEquipmentMaxMana(pet, equipment));
  if (current < skill.manaCost) return `${skill.name} costs ${skill.manaCost} Mana; this pet has ${current}.`;
  return null;
}

export function planPetSpellAttack(
  pet: PetCompanion,
  floor: number,
  skill: Skill,
  registry: ContentRegistry,
  equipment?: PetEquipment,
): PetSpellAttackPlan | null {
  assertFloor(floor);
  if (petSpellProblem(pet, skill, equipment)) return null;
  const profile = registry.getAttack(skill.id);
  if (!profile || profile.manaCost === null) return null;

  const rank = Math.max(0, Math.min(skill.maxRank ?? 20, petAssignedSpellRank(pet, skill, floor) + (equipment?.modifiers.skillRankBonuses[skill.id] ?? 0)));
  const toHitStat = profile.toHitStat;
  const toHitMod = statModForScore((equipment?.enhancedStats ?? pet.unenhancedStats)[toHitStat]);
  const damageStat = profile.baseDamage.stat;
  const damageMod = damageStat ? statModForScore((equipment?.enhancedStats ?? pet.unenhancedStats)[damageStat]) : null;
  const dice = rank >= 5 && profile.rank5.addedDamageDice
    ? `${profile.baseDamage.dice} + ${profile.rank5.addedDamageDice} (Rank 5)`
    : profile.baseDamage.dice;
  const notes: string[] = [...profile.limitations];
  if (profile.cooldown) notes.push(`Cooldown: ${profile.cooldown}`);
  if (profile.range?.feet) notes.push(`Range ${profile.range.feet} ft`);
  if (profile.range?.kind === 'melee') notes.push('Range: Melee (5 ft)');
  if (profile.area) notes.push(`${profile.area.feet} ft ${profile.area.kind} radius`);
  if (rank >= 5) notes.push(...profile.rank5.notes);
  notes.push(pet.spellRanks[skill.id] ? `Pet Spell Rank = GM override ${rank}; pets pay no Class/Favored surcharge.` : 'Pet Spell Rank = current Floor by default; pets pay no Class/Favored surcharge.');

  return {
    skill,
    equipment,
    profile,
    rank,
    toHitStat,
    toHitMod,
    toHitFormula: `d20 + ${STAT_LABELS[toHitStat].abbr} Mod (${signed(toHitMod)}) + ${skill.name} Rank (${signed(rank)})`,
    damageFormula: `${dice}${damageStat && damageMod !== null ? ` + ${STAT_LABELS[damageStat].abbr} Mod (${signed(damageMod)})` : ''} ${profile.baseDamage.type}`,
    manaCost: profile.manaCost,
    notes,
  };
}

export function rollPetSpellAttackCheck(
  roller: Roller,
  plan: PetSpellAttackPlan,
  mode: RollMode,
  targetEvade: number | null = null,
): AttackCheckOutcome {
  return resolveAttackCheck(roller, {
    skillId: `pet-spell-${plan.skill.id}`,
    skillName: plan.skill.name,
    toHitStat: plan.toHitStat,
    toHitStatAbbr: STAT_LABELS[plan.toHitStat].abbr,
    toHitStatMod: plan.toHitMod,
    rank: plan.rank,
    mode: petCheckMode(plan.equipment, mode, { kind: 'attack', skillId: plan.skill.id, stat: plan.toHitStat }),
    extraComponents: petCheckBonuses(plan.equipment, { kind: 'attack', skillId: plan.skill.id, stat: plan.toHitStat }),
  }, targetEvade);
}

export function rollPetSpellAttackDamage(
  roller: Roller,
  pet: PetCompanion,
  floor: number,
  plan: PetSpellAttackPlan,
  outcome: AttackCheckOutcome,
  registry: ContentRegistry,
): DamageRollResult {
  const damageStat = plan.profile.baseDamage.stat;
  return rollDamage(roller, {
    baseDice: plan.profile.baseDamage.dice,
    rank5Dice: plan.profile.rank5.addedDamageDice,
    rank: plan.rank,
    damageStat,
    damageStatAbbr: damageStat ? STAT_LABELS[damageStat].abbr : null,
    damageStatMod: damageStat ? statModForScore((plan.equipment?.enhancedStats ?? pet.unenhancedStats)[damageStat]) : null,
    damageType: plan.profile.baseDamage.type,
    degree: outcome.resolution ? outcome.resolution.degree : null,
    floor,
    rankDamageDieTable: registry.attackContent.rankDamageDiceTable,
  });
}

export function payPetSpellCost(pet: PetCompanion, skill: Skill, now: string, equipment?: PetEquipment): PetSupportSpellCast {
  const problem = petSpellProblem(pet, skill, equipment);
  if (problem) return { ok: false, reason: problem };
  const spend = spendPetMana(pet, skill.manaCost ?? 0, now, equipment);
  if (!spend.ok) return spend;
  const manaSpent = skill.manaCost ?? 0;
  return { ok: true, pet: spend.pet, manaSpent, lines: [`${skill.name}: -${manaSpent} Mana.`] };
}

/** Executes the support effects this rules engine actually models. */
export function castPetSupportSpell(pet: PetCompanion, floor: number, skill: Skill, now: string, equipment?: PetEquipment): PetSupportSpellCast {
  assertFloor(floor);
  const problem = petSpellProblem(pet, skill, equipment);
  if (problem) return { ok: false, reason: problem };
  if (!skill.supportEffect && !isManualCustomSpell(skill)) return { ok: false, reason: `${skill.name} has no automated pet support effect yet.` };

  const spend = spendPetMana(pet, skill.manaCost ?? 0, now, equipment);
  if (!spend.ok) return spend;
  let next = spend.pet;
  const manaSpent = skill.manaCost ?? 0;
  const lines = [`${skill.name}: -${manaSpent} Mana.`];

  if (skill.supportEffect?.kind === 'heal-slots' && skill.supportEffect.target === 'self') {
    const before = next.healthBarSlotsRemaining;
    next = healPetHealthSlots(next, skill.supportEffect.slots, now);
    lines.push(`Healed ${next.healthBarSlotsRemaining - before} Health Bar slot(s).`);
  } else if (skill.supportEffect?.kind === 'heal-full' && skill.supportEffect.target === 'pet') {
    const before = next.healthBarSlotsRemaining;
    next = { ...next, healthBarSlotsRemaining: petMaxHealthSlots(next), updatedAt: now };
    lines.push(`Healed ${next.healthBarSlotsRemaining - before} Health Bar slot(s) to full.`);
  } else if (isManualCustomSpell(skill)) {
    lines.push(`The GM resolves the described effect manually: ${skill.description ?? ''}`);
  } else {
    return { ok: false, reason: `${skill.name} has a support effect this pet caster does not automate.` };
  }
  return { ok: true, pet: next, manaSpent, lines };
}

export function petSpellCooldownSourceId(petId: string, spellId: string): string {
  return `pet:${petId}:spell:${spellId}`;
}

export function petSpellCooldownState(
  petId: string,
  plan: PetSpellAttackPlan,
  runtime: RuntimeState | null,
): { blocked: boolean; reason: string | null } {
  if (!plan.profile.cooldown || !runtime) return { blocked: false, reason: null };
  if (!isOnCooldown(runtime, petSpellCooldownSourceId(petId, plan.skill.id))) return { blocked: false, reason: null };
  return { blocked: true, reason: `${plan.skill.name} is on cooldown (${plan.profile.cooldown}).` };
}

export function startPetSpellCooldown(
  petId: string,
  plan: PetSpellAttackPlan,
  runtime: RuntimeState,
): RuntimeState {
  const label = plan.profile.cooldown;
  if (!label) return runtime;
  const scope = cooldownScopeFor(label);
  if (!scope) return runtime;
  if ((scope === 'round' || scope === 'scene') && !runtime.encounter) return runtime;
  const amount = /(\d+)\s*hour/.exec(label.toLowerCase());
  return startCooldown(runtime, {
    sourceId: petSpellCooldownSourceId(petId, plan.skill.id),
    scope,
    ...(amount ? { amount: Number(amount[1]) } : {}),
  });
}
